<script setup lang="ts">
import AnimatedLogoLoader from '@/components/createdUi/AnimatedLogoLoader.vue'
import ConfirmDialog from '@/components/dialogs/ConfirmDialog.vue'
import { usePermissionsStore } from '@/modules/permissions/stores/permissionsStore'
import { ERouteNames } from '@/router/ERouteNames'
import { refDebounced } from '@vueuse/core'
import AddEmployeeDialog from '../AddEmployeeDialog.vue'
import EditEmployeeDialog from '../EditEmployeeDialog.vue'
import ImportHistoryModal from '@/modules/import/components/ImportHistoryModal.vue'
import { useStaffSettingsStore } from '../../stores/staffSettingsStore'
import type { IStaffDepartmentTreeNode } from '../../types/settings/IStaffDepartmentSettings'
import DepartmentFormModal from './DepartmentFormModal.vue'
import DepartmentTreeNode from './DepartmentTreeNode.vue'
import StaffSettingsEmployeePanel from './StaffSettingsEmployeePanel.vue'
import StaffSettingsSplit from './StaffSettingsSplit.vue'
import StructureModal from '../StructureModal.vue'
import MoveEmployeesModal from './MoveEmployeesModal.vue'
import StaffSettingsEntityRow from './StaffSettingsEntityRow.vue'

const staffSettingsStore = useStaffSettingsStore()
const permissionsStore = usePermissionsStore()
const route = useRoute()
const router = useRouter()

const canImportEmployees = computed(() =>
  !!permissionsStore.modules.employee_management?.employee_edit,
)
const canImportDepartments = computed(() =>
  !!permissionsStore.modules.employee_management?.department_edit,
)

const canEditEmployees = computed(() =>
  !!permissionsStore.modules.employee_management?.employee_edit,
)
const editEmployeeOpen = ref(false)
const editEmployeeId = ref<string | null>(null)

function openEditEmployee(id: string) {
  editEmployeeId.value = id
  editEmployeeOpen.value = true
}

const dragEnabled = ref(false)
const structureOpen = ref(false)
const historyOpen = ref(false)
const historyImportType = ref<'employees' | 'departments'>('employees')
const createOpen = ref(false)
const addEmployeeOpen = ref(false)
const moveOpen = ref(false)
const moving = ref(false)
const deleteOpen = ref(false)
const deleteTarget = ref<IStaffDepartmentTreeNode | null>(null)

const selectedId = ref<string | null>(null)
const collapsedIds = ref<string[]>([])
const formDepartmentId = ref<string | null>(null)
const dragOverId = ref<string | null>(null)
const selectedEmployeeIds = ref<string[]>([])

const treeSearch = ref('')
const treeSearchDebounced = refDebounced(treeSearch, 300)
const treeSearchLoading = ref(false)
let treeSearchSeq = 0

const employeeHeaders = [
  { title: 'ФИО', key: 'fio', sortable: true },
  { title: 'Должность', key: 'position_name', sortable: true, filterType: 'tree', filterKey: 'positions' },
  { title: 'Телефон', key: 'phone_number', sortable: true },
  { title: 'Email', key: 'email', sortable: true },
]

const selectedEmployees = computed(() =>
  staffSettingsStore.departmentEmployees.filter(item => selectedEmployeeIds.value.includes(String(item.id))),
)

const WITHOUT_DEPT_ID = 'without-department'
const withoutDepartmentCount = computed(() => staffSettingsStore.withoutDepartmentInfo?.employee_count ?? 0)
const hasWithoutDepartment = computed(() => withoutDepartmentCount.value > 0)
const isWithoutDeptSelected = computed(() => selectedId.value === WITHOUT_DEPT_ID)

const displayDepartmentsTree = computed<IStaffDepartmentTreeNode[]>(() =>
  (treeSearchDebounced.value || '').trim()
    ? staffSettingsStore.searchDepartmentsTree
    : staffSettingsStore.departmentsTree,
)

async function refreshTreeData() {
  await Promise.all([
    staffSettingsStore.fetchDepartmentsTree(),
    staffSettingsStore.fetchWithoutDepartmentInfo(),
  ])
  if ((treeSearchDebounced.value || '').trim())
    await staffSettingsStore.fetchDepartmentsTree(treeSearchDebounced.value, { silent: true })
}

watch(treeSearchDebounced, async value => {
  const seq = ++treeSearchSeq
  treeSearchLoading.value = true
  try {
    await staffSettingsStore.fetchDepartmentsTree(value, { silent: true })
  } finally {
    if (seq === treeSearchSeq)
      treeSearchLoading.value = false
  }
})

onMounted(async () => {
  await Promise.all([
    staffSettingsStore.fetchDepartmentsTree(),
    staffSettingsStore.fetchWithoutDepartmentInfo(),
  ])
  const first = staffSettingsStore.departmentsTree[0]
  if (!selectedId.value && first)
    selectedId.value = String(first.id)
})

function findNode(nodes: IStaffDepartmentTreeNode[], id: string): IStaffDepartmentTreeNode | null {
  for (const node of nodes) {
    if (String(node.id) === id) return node
    const nested = findNode(node.children || [], id)
    if (nested) return nested
  }
  return null
}

function containsId(node: IStaffDepartmentTreeNode, id: string): boolean {
  return (node.children || []).some(child => String(child.id) === id || containsId(child, id))
}

function toggle(id: string) {
  collapsedIds.value = collapsedIds.value.includes(id)
    ? collapsedIds.value.filter(item => item !== id)
    : [...collapsedIds.value, id]
}

function openImportEmployees() {
  router.push({
    name: ERouteNames.IMPORT_DATA,
    params: { importType: 'employees' },
    query: { from: route.fullPath },
  })
}

function openHistoryEmployees() {
  historyImportType.value = 'employees'
  historyOpen.value = true
}

function openImportDepartments() {
  router.push({
    name: ERouteNames.IMPORT_DATA,
    params: { importType: 'departments' },
    query: { from: route.fullPath },
  })
}

function openHistoryDepartments() {
  historyImportType.value = 'departments'
  historyOpen.value = true
}

function openCreate() {
  formDepartmentId.value = null
  createOpen.value = true
}

function openEdit(id: string) {
  formDepartmentId.value = id
  createOpen.value = true
}

function askDelete(id: string) {
  deleteTarget.value = findNode(staffSettingsStore.departmentsTree, id)
  deleteOpen.value = Boolean(deleteTarget.value)
}

async function confirmDelete(ok: boolean) {
  deleteOpen.value = false
  if (!ok || !deleteTarget.value) return
  const id = String(deleteTarget.value.id)
  const removed = await staffSettingsStore.deleteDepartment(id)
  if (removed && (treeSearchDebounced.value || '').trim())
    await staffSettingsStore.fetchDepartmentsTree(treeSearchDebounced.value, { silent: true })
  if (removed && selectedId.value === id)
    selectedId.value = null
  deleteTarget.value = null
}

function onDeptDragStart(id: string, event: DragEvent) {
  event.stopPropagation()
  event.dataTransfer?.setData('application/x-staff-department', id)
  if (event.dataTransfer)
    event.dataTransfer.effectAllowed = 'move'
}

async function onDrop(targetId: string, event: DragEvent) {
  dragOverId.value = null
  const employeesRaw = event.dataTransfer?.getData('application/x-staff-employees')
  if (employeesRaw) {
    const ids = JSON.parse(employeesRaw) as string[]
    if (!ids.length) return
    const moved = targetId === WITHOUT_DEPT_ID
      ? await staffSettingsStore.unassignDepartmentEmployees(ids)
      : await staffSettingsStore.addDepartmentEmployees(targetId, ids)
    if (moved && selectedId.value)
      await staffSettingsStore.fetchDepartmentEmployees(selectedId.value)
    selectedEmployeeIds.value = []
    return
  }

  // Перемещение самого отделения в «Без отделения» запрещено
  if (targetId === WITHOUT_DEPT_ID) return

  const draggedId = event.dataTransfer?.getData('application/x-staff-department')
  if (!draggedId || draggedId === targetId) return
  const dragged = findNode(staffSettingsStore.departmentsTree, draggedId)
  if (dragged && containsId(dragged, targetId)) return
  try {
    await staffSettingsStore.updateDepartment(draggedId, { parent_id: targetId })
    if ((treeSearchDebounced.value || '').trim())
      await staffSettingsStore.fetchDepartmentsTree(treeSearchDebounced.value, { silent: true })
  } catch {
    // Ошибка уже показана стором
  }
}

async function fetchEmployees(
  search: string,
  cursor?: string,
  limit?: number,
  sort?: string | null,
  filters?: Record<string, any>,
) {
  if (!selectedId.value) return
  await staffSettingsStore.fetchDepartmentEmployees(selectedId.value, search, cursor, limit, sort, filters)
}

async function onEmployeeCreated() {
  await refreshTreeData()
  if (selectedId.value)
    await staffSettingsStore.fetchDepartmentEmployees(selectedId.value)
}

async function onDepartmentSaved() {
  await refreshTreeData()
}

function removeMovedEmployee(id: string) {
  selectedEmployeeIds.value = selectedEmployeeIds.value.filter(item => item !== id)
}

async function moveSelected(targetId: string) {
  const ids = selectedEmployees.value.map(item => String(item.id))
  if (!selectedId.value || !ids.length || moving.value) return
  moving.value = true
  try {
    const moved = targetId === WITHOUT_DEPT_ID
      ? await staffSettingsStore.unassignDepartmentEmployees(ids)
      : await staffSettingsStore.addDepartmentEmployees(targetId, ids)
    if (!moved) return
    await staffSettingsStore.fetchDepartmentEmployees(selectedId.value)
    selectedEmployeeIds.value = []
    moveOpen.value = false
  } finally {
    moving.value = false
  }
}
</script>

<template>
  <div class="d-flex flex-column ga-4">
    <div class="d-flex align-center justify-space-between ga-3 flex-wrap">
      <div class="d-flex align-center ga-2 flex-wrap">
        <VMenu
          v-if="canImportEmployees || canImportDepartments"
          location="bottom end"
          content-class="entity-form-menu"
        >
          <template #activator="{ props: menuProps }">
            <VBtn
              v-bind="menuProps"
              color="primary"
              prepend-icon="ri-download-2-line"
              append-icon="ri-arrow-down-s-line"
            >
              Импортировать данные
            </VBtn>
          </template>
          <VList density="compact" min-width="220">
            <template v-if="canImportEmployees">
              <VListSubheader class="text-uppercase text-caption font-weight-bold text-medium-emphasis">
                Сотрудники
              </VListSubheader>
              <VListItem
                title="Начать импорт"
                @click="openImportEmployees"
              />
              <VListItem
                title="История импорта"
                @click="openHistoryEmployees"
              />
            </template>

            <VDivider v-if="canImportEmployees && canImportDepartments" class="my-1" />

            <template v-if="canImportDepartments">
              <VListSubheader class="text-uppercase text-caption font-weight-bold text-medium-emphasis">
                Отделения
              </VListSubheader>
              <VListItem
                title="Начать импорт"
                @click="openImportDepartments"
              />
              <VListItem
                title="История импорта"
                @click="openHistoryDepartments"
              />
            </template>
          </VList>
        </VMenu>
      </div>
      <VBtn
        variant="outlined"
        color="secondary"
        class="bg-surface"
        prepend-icon="ri-git-merge-line"
        @click="structureOpen = true"
      >
        Структура
      </VBtn>
    </div>

    <StaffSettingsSplit>
      <template #header>
        <div class="d-flex align-center justify-space-between ga-4 flex-wrap pa-4">
          <span class="body-1-500 staff-dnd-hint">
            Вы можете включить возможность перемещать подразделения и сотрудников перетаскиванием.
          </span>
          <VSwitch
            v-model="dragEnabled"
            hide-details
            density="compact"
            color="primary"
            inset
          >
            <template #label>
              <span class="body-1-500 staff-dnd-hint">Перетаскивание</span>
            </template>
          </VSwitch>
        </div>
        <VDivider />
      </template>
      <template #left>
        <div class="d-flex flex-column ga-3">
          <VBtn block color="primary" prepend-icon="ri-add-line" @click="openCreate">
            Добавить отделение
          </VBtn>
          <div class="d-flex align-center ga-2 align-self-stretch">
            <VTextField
              :model-value="treeSearch"
              placeholder="Найти..."
              prepend-inner-icon="ri-search-line"
              variant="underlined"
              density="compact"
              hide-details
              clearable
              class="staff-settings-search search-underlined"
              @update:model-value="treeSearch = $event ?? ''"
            />
          </div>
          <div v-if="treeSearchLoading" class="staff-tree-loader d-flex align-center justify-center">
            <AnimatedLogoLoader />
          </div>
          <div
            v-else-if="(treeSearchDebounced || '').trim() && !displayDepartmentsTree.length"
            class="text-body-2 text-medium-emphasis text-center"
          >
            Ничего не найдено
          </div>
          <template v-else>
          <DepartmentTreeNode
            v-for="node in displayDepartmentsTree"
            :key="node.id"
            class="align-self-stretch w-100"
            :node="node"
            :selected-id="selectedId"
            :collapsed-ids="collapsedIds"
            :drag-enabled="dragEnabled"
            :editing-id="null"
            :draft-name="''"
            :drag-over-id="dragOverId"
            :search="treeSearch"
            @select="selectedId = $event"
            @toggle="toggle"
            @rename="openEdit"
            @remove="askDelete"
            @dragstart="onDeptDragStart"
            @dragover="dragOverId = $event"
            @drop="onDrop"
          />
          </template>

          <!-- Блок «Без отделения» (вне структуры), отображается если employee_count > 0 -->
          <template v-if="hasWithoutDepartment">
            <div class="my-1 border-t" />
            <div class="text-caption text-medium-emphasis text-uppercase font-weight-medium px-2">
              Вне структуры
            </div>
            <StaffSettingsEntityRow
              name="Без отделения"
              :count="withoutDepartmentCount"
              :selected="isWithoutDeptSelected"
              :indent="12"
              menu="none"
              :draggable="false"
              :drop-enabled="dragEnabled"
              :drag-over="dragOverId === WITHOUT_DEPT_ID"
              @select="selectedId = WITHOUT_DEPT_ID"
              @dragover="dragOverId = WITHOUT_DEPT_ID"
              @drop="onDrop(WITHOUT_DEPT_ID, $event)"
            />
          </template>
        </div>
      </template>
      <template #right>
        <StaffSettingsEmployeePanel
          v-model:selected-ids="selectedEmployeeIds"
          :items="staffSettingsStore.departmentEmployees"
          :total="staffSettingsStore.departmentEmployeesCount"
          :next-cursor="staffSettingsStore.departmentEmployeesNext"
          :headers="employeeHeaders"
          :fetch-page="fetchEmployees"
          :reload-key="selectedId"
          :drag-enabled="dragEnabled"
          :can-add="!isWithoutDeptSelected"
          move-label="Переместить"
          move-tooltip="Переместить выбранных сотрудников в другое подразделение"
          add-label="Добавить"
          empty-text="Выберите отделение"
          storage-key="staff-settings-department-employees"
          :row-editable="canEditEmployees"
          @move="moveOpen = true"
          @add="addEmployeeOpen = true"
          @edit="openEditEmployee"
        />
      </template>
    </StaffSettingsSplit>

    <StructureModal v-model="structureOpen" />
    <ImportHistoryModal
      v-model="historyOpen"
      :import-type="historyImportType"
    />
    <DepartmentFormModal
      v-model="createOpen"
      :tree="staffSettingsStore.departmentsTree"
      :department-id="formDepartmentId"
      :parent-id="isWithoutDeptSelected ? null : selectedId"
      @saved="onDepartmentSaved"
    />
    <MoveEmployeesModal
      v-model="moveOpen"
      :employees="selectedEmployees"
      :tree="staffSettingsStore.departmentsTree"
      :loading="moving"
      @confirm="moveSelected"
      @remove="removeMovedEmployee"
      @remove-all="selectedEmployeeIds = []"
    />
    <AddEmployeeDialog
      v-model="addEmployeeOpen"
      :department-id="selectedId"
      @created="onEmployeeCreated"
    />
    <EditEmployeeDialog
      v-model="editEmployeeOpen"
      :employee-id="editEmployeeId"
    />
    <ConfirmDialog
      v-model:is-dialog-visible="deleteOpen"
      :confirmation-question="`Удалить подразделение «${deleteTarget?.name || ''}»?`"
      confirmation-text="Подразделение будет удалено. Вложенные подразделения останутся и появятся в корне списка."
      confirm-btn="Удалить"
      reject-btn="Отменить"
      @confirm="confirmDelete"
    />
  </div>
</template>

<style scoped lang="scss">
.staff-dnd-hint {
  color: rgba(var(--v-theme-text-primary), var(--v-medium-emphasis-opacity));
}

.staff-settings-search {
  flex: 1 1 auto;
  min-inline-size: 0;
  max-inline-size: 100%;
}

.staff-tree-loader {
  min-height: 160px;
}

:deep(.search-underlined.v-text-field) {
  .v-field__control {
    height: 36px !important;
  }

  .v-field__prepend-inner {
    align-self: center !important;
    padding-top: 0 !important;
    padding-bottom: 0 !important;
  }

  .v-field__input {
    padding-top: 0 !important;
    padding-bottom: 0 !important;
    min-height: 36px !important;
    align-self: center !important;
    display: flex !important;
    align-items: center !important;
  }
}
</style>
